// Configure your API base URL here - adjust the subdomain based on your environment
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_URL;

export interface StatsResponse {
    status: 'success' | 'error';
    data?: {
        singlePlayerGames: number;
        multiplayerGames: number;
        totalPlayers: number;
        onlinePlayers: number;
        activePaidSubscriptions: number;
        activeFreeTrials: number;
        playerTrialInvites: number;
    };
    message?: string;
}

export interface NewsfeedItem {
    id: string;
    title: string;
    description?: string;
    htmlUrl: string;
    mediaUrl?: string;
    mediaType?: string;
    isActive: boolean;
    created: string;
    updated?: string;
}

export interface NewsfeedResponse {
    status: 'success' | 'error';
    data?: NewsfeedItem[];
    message?: string;
}

export interface CreateNewsfeedItemRequest {
    title: string;
    description?: string;
    mediaUrl?: string;
    mediaType?: string;
    isActive?: boolean;
}

export interface UpdateNewsfeedItemRequest {
    title?: string;
    description?: string;
    mediaUrl?: string;
    mediaType?: string;
    isActive?: boolean;
}

export interface NewsfeedItemResponse {
    status: 'success' | 'error';
    data?: NewsfeedItem;
    message?: string;
}

export interface FeedbackItem {
    id: string;
    playerId: string;
    text: string;
    created: string;
}

export interface FeedbackResponse {
    status: 'success' | 'error';
    data?: {
        feedback: FeedbackItem[];
        count: number;
    };
    message?: string;
}

export interface UploadMediaRequest {
    newsfeedItemId: string;
    filename: string;
    contentType: string;
}

export interface UploadMediaResponse {
    status: 'success' | 'error';
    data?: {
        mediaUrl: string;
        signedUrl: string;
        mediaType: string;
        s3Key: string;
    };
    message?: string;
}

export const adminApi = {
    getStats: async (): Promise<StatsResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/stats`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get stats',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Get stats error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    getAllNewsfeedItems: async (): Promise<NewsfeedResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get newsfeed items',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Get newsfeed items error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    createNewsfeedItem: async (
        request: CreateNewsfeedItemRequest
    ): Promise<NewsfeedItemResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to create newsfeed item',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Create newsfeed item error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    updateNewsfeedItem: async (
        id: string,
        request: UpdateNewsfeedItemRequest
    ): Promise<NewsfeedItemResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to update newsfeed item',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Update newsfeed item error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    deleteNewsfeedItem: async (
        id: string
    ): Promise<{ status: 'success' | 'error'; message?: string }> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to delete newsfeed item',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Newsfeed item deleted successfully',
            };
        } catch (error) {
            console.error('Delete newsfeed item error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    resetUserNewsfeedTracking: async (
        playerId: string
    ): Promise<{ status: 'success' | 'error'; message?: string }> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed/reset/${playerId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to reset user newsfeed tracking',
                };
            }

            return {
                status: 'success',
                message: data.message || 'User newsfeed tracking reset successfully',
            };
        } catch (error) {
            console.error('Reset user newsfeed tracking error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    getAllFeedback: async (limit?: number): Promise<FeedbackResponse> => {
        try {
            const url = new URL(`${API_BASE_URL}/admin/feedback`);
            if (limit) {
                url.searchParams.append('limit', limit.toString());
            }

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get feedback',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Get feedback error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    uploadNewsfeedMedia: async (request: UploadMediaRequest): Promise<UploadMediaResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed/upload-media`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get upload URL',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Upload media error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },
};
